import React, { useCallback, useEffect, useState } from "react";
import { TallyForm } from "react-tally";
import diakoniaLogo from "./assets/diakonia.png";
import diakoniaLogoFooter from "./assets/diakonia-footer.png";
import bg from "./assets/bg.png";
import x from "./assets/x.png";
import fb from "./assets/fb.png";
import linkedin from "./assets/linkedin.png";

export default function App() {

  function amountToPay(targetAmount) {
    const feeRate = 0.015;
    const flatFee = 100;

    const raw = (targetAmount + flatFee) / (1 - feeRate);
    // Round up to nearest 10 naira
    const rounded = Math.ceil(raw / 10) * 10;

    return rounded;
  }
  const baseUrl = "https://diakoniaschoolofministry.com";
  const PAYSTACK_PUBLIC_KEY =
    process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || "pk_test_replace_with_live_key";
  const PAYSTACK_SPLIT_CODE =
    process.env.REACT_APP_PAYSTACK_SPLIT_CODE || "pk_test_replace_with_live_key";
  const APPLICATION_FEE_NGN = process.env.REACT_APP_PAYMENT_AMOUNT || 20000;
  const paystackAmountKobo = amountToPay(Number(APPLICATION_FEE_NGN)) * 100;
  const socialLinks = [
    {
      name: "X (Twitter)",
      href: "#",
      icon: <img src={x} alt="X (Twitter)" className="w-4 h-4" aria-hidden="true" />,
    },
    {
      name: "Facebook",
      href: "#",
      icon: <img src={fb} alt="Facebook" className="w-4 h-4" aria-hidden="true" />,
    },
    {
      name: "LinkedIn",
      href: "#",
      icon: <img src={linkedin} alt="LinkedIn" className="w-4 h-4" aria-hidden="true" />,
    },
  ];

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("idle");
  const [paymentReference, setPaymentReference] = useState(null);
  const [paymentError, setPaymentError] = useState("");
  const [paystackReady, setPaystackReady] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const [lastSubmission, setLastSubmission] = useState(null);

  useEffect(() => {
    const scriptId = "paystack-inline";
    if (document.getElementById(scriptId)) {
      setPaystackReady(true);
      return;
    }
    const script = document.createElement("script");
    script.id = scriptId;
    script.src = "https://js.paystack.co/v1/inline.js";
    script.onload = () => setPaystackReady(true);
    script.onerror = () =>
      setPaymentError("Unable to load Paystack checkout. Please refresh and try again.");
    document.body.appendChild(script);
  }, []);

  const extractEmailFromSubmission = (submissionEvent) => {
    const payload = submissionEvent?.payload;
    if (!payload) return undefined;

    const immediateEmail =
      payload?.data?.email || payload?.email || payload?.response?.email;
    if (typeof immediateEmail === "string" && immediateEmail.includes("@")) {
      return immediateEmail;
    }

    const answers =
      payload?.response?.data ||
      payload?.response?.answers ||
      payload?.data?.fields ||
      payload?.fields ||
      payload?.answers;

    if (Array.isArray(answers)) {
      for (const answer of answers) {
        const candidates = [
          answer?.value,
          answer?.email,
          answer?.text,
          answer?.answer,
          answer?.answer?.email,
          answer?.answer?.value,
        ];
        for (const candidate of candidates) {
          if (typeof candidate === "string" && candidate.includes("@")) {
            return candidate;
          }
        }
      }
    }
    return undefined;
  };

  const openPaystackPayment = useCallback((submissionPayload) => {
    if (!window.PaystackPop || !paystackReady) {
      setPaymentError("Payment widget is not ready. Please refresh and try again.");
      setPaymentStatus("error");
      return;
    }

    setPaymentError("");
    setPaymentStatus("processing");
    const email =
      extractEmailFromSubmission(submissionPayload) || "applications@diakoniaschool.com";
    const reference = `DSOM-${Date.now()}`;
    const handler = window.PaystackPop.setup({
      key: PAYSTACK_PUBLIC_KEY,
      email,
      amount: paystackAmountKobo,
      currency: "NGN",
      ref: reference,
      split_code: PAYSTACK_SPLIT_CODE,
      label: "Diakonia School Application Fee",
      callback: (response) => {
        setPaymentStatus("success");
        setPaymentReference(response.reference);
        setPendingSubmission(null);
      },
      onClose: () => {
        setPaymentStatus((status) => (status === "success" ? status : "cancelled"));
        setPendingSubmission(null);
      },
    });
    handler.openIframe();
  }, [PAYSTACK_PUBLIC_KEY, paystackAmountKobo, paystackReady]);

  useEffect(() => {
    if (paystackReady && pendingSubmission) {
      openPaystackPayment(pendingSubmission);
      setPendingSubmission(null);
    }
  }, [paystackReady, pendingSubmission, openPaystackPayment]);

  const handleFormSubmission = (submissionEvent) => {
    setFormSubmitted(true);
    setPaymentStatus("pending");
    setPaymentReference(null);
    setPaymentError("");
    setPendingSubmission(submissionEvent);
    setLastSubmission(submissionEvent);
    if (paystackReady) {
      openPaystackPayment(submissionEvent);
    }
  };

  const renderPaymentStatus = () => {
    if (!formSubmitted) return null;

    const baseClass =
      "mt-4 text-center text-sm font-medium px-4 py-3 rounded-md border border-[#D7A477]";
    switch (paymentStatus) {
      case "pending":
        return (
          <p className={`${baseClass} bg-[#FFF8EA] text-[#903D1D]`}>
            Preparing your secure Paystack checkout…
          </p>
        );
      case "processing":
        return (
          <p className={`${baseClass} bg-[#FFF6E0] text-[#903D1D]`}>
            Payment window opened. Please complete the ₦{Number(APPLICATION_FEE_NGN).toLocaleString()}
            {" "}fee to finalize your application.
          </p>
        );
      case "success":
        return (
          <p className={`${baseClass} bg-[#E7F8ED] text-[#1E6D3C]`}>
            Payment received successfully. Reference: {paymentReference}
          </p>
        );
      case "cancelled":
        return (
          <p className={`${baseClass} bg-[#FDEFEF] text-[#A11D2B]`}>
            You closed the payment window. Click “Retry Payment” to try again.
          </p>
        );
      case "error":
        return (
          <p className={`${baseClass} bg-[#FDEFEF] text-[#A11D2B]`}>
            {paymentError || "Something went wrong while starting the payment."}
          </p>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF1D4] text-[#6A3714]">
      {/* Navbar */}
      <nav className="bg-[#8F3E1B] text-[#FBF2D4] rounded-md mx-4 md:mx-20 my-4 py-3 md:py-4 px-4 md:px-6 flex justify-between items-center">
        <a href={baseUrl} className="font-bold text-lg flex items-center gap-2">
          <img width={160} src={diakoniaLogo} alt="Diakonia Logo" />
        </a>

        <div className="hidden md:flex gap-8 text-lg">
          <a href={baseUrl} className="hover:opacity-80">
            Home
          </a>
          <a href={`${baseUrl}/programme`} className="hover:opacity-80">
            Programme
          </a>
          <a href={`${baseUrl}/contact`} className="hover:opacity-80">
            Contact
          </a>
        </div>

        <button className="bg-[#FBF2D4] text-[#8F3E1B] font-semibold px-4 py-2 rounded-md shadow">
          ENROLL NOW
        </button>
      </nav>

      {/* Header */}
      <header className="text-center text-[#903D1D] py-10 md:py-16 px-4">
        <h1 className="text-3xl md:text-5xl font-extrabold mb-4 font-[Heavitas]">APPLICATION FORM</h1>
        <p className="max-w-2xl mx-auto text-lg">
          Welcome to Diakonia School of Ministry Application Form.
          <br />
          Please complete this form carefully with accurate details as you can only fill it once.
        </p>
      </header>

      <div
        className="relative"
        style={{ backgroundImage: `url(${bg})`, backgroundSize: "cover" }}
      >
        {/* Application Fee Notice */}
        <section className="py-8 md:py-10 px-4 md:px-6 mx-auto max-w-6xl flex justify-center">
          <div className="bg-[#FBF1D4] border border-[#D7A477] rounded-lg p-4 md:p-6 w-full shadow">
            <h2 className="text-xl text-[#903D1D] font-semibold mb-2">Application Fee Required</h2>
            <p className="text-[#635D54]">
              After submitting this form, you will be prompted to pay the non-refundable application fee of <strong>₦{Number(APPLICATION_FEE_NGN).toLocaleString()}</strong>.
Additional processing charges may apply.
            </p>
          </div>
        </section>

        {/* Tally Form */}
        <section className="flex py-8 md:py-10 px-4 md:px-6 justify-center max-w-6xl mx-auto">
          <div className="w-full bg-[rgba(255,255,255,0.8)] p-4 md:p-10 rounded-lg">
            <TallyForm formId="OD1zpp" className="w-full" onSubmission={handleFormSubmission} />
            {renderPaymentStatus()}
            {formSubmitted && paymentStatus !== "success" && (
              <div className="flex flex-col items-center gap-3 mt-6">
                <p className="text-sm text-[#903D1D]">
                  The Paystack webview should open automatically. If you closed it, launch it again
                  below.
                </p>
                <button
                  type="button"
                  className="bg-[#8F3E1B] text-[#FBF2D4] font-semibold px-5 py-2 rounded-md shadow hover:opacity-90"
                  onClick={() => lastSubmission && openPaystackPayment(lastSubmission)}
                  disabled={!lastSubmission || paymentStatus === "processing"}
                >
                  {paymentStatus === "processing" ? "Awaiting Payment…" : "Retry Payment"}
                </button>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="py-12 px-6 mt-16">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <div className="font-bold text-lg mb-2 flex items-center gap-2">
              <img width={160} src={diakoniaLogoFooter} alt="Diakonia Logo" />
            </div>
            <p className="text-[#635D54]">Equipping Kingdom Ambassadors for Global Impact</p>
            <div className="flex gap-4 mt-4 text-xl text-[#8F3E1B]">
              {socialLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="p-2 rounded-full hover:bg-[#8F3E1B] hover:text-[#FBF2D4] transition-colors"
                  aria-label={link.name}
                >
                  <span className="sr-only">{link.name}</span>
                  {link.icon}
                </a>
              ))}
            </div>
          </div>

          <div className="text-[#242019]">
            <p>Lagos, Nigeria</p>
            <p className="mt-2">diakonia@gloryrealmsministries.org</p>
            <p className="mt-2">+2348058844798</p>
          </div>

          <div className="space-y-2 text-[#242019]">
            <a href="#" className="block hover:opacity-70">
              About Us
            </a>
            <a href="#" className="block hover:opacity-70">
              Programme
            </a>
            <a href="#" className="block hover:opacity-70">
              Contact us
            </a>
          </div>
        </div>

        <div className="text-center mt-10 text-sm text-[#635D54]">
          Copyright © Diakonia — All Rights Reserved.
        </div>
      </footer>
    </div>
  );
}
